import type {
  Account, AdminDeveloper, AdminGame, AdminGameDetail, ApiKey, AuditLog, Balance, Bank, Clock, Credential, Currency,
  DevSession, Developer, DeveloperOverview, Env, Game, HistoryItem, Hold, JobRun, Limits, LimitPolicy, NameEnquiry, Overview, Page, Player,
  ReconReport, SignInResult, Transaction, FeeRule, FeeRuleInput, FeeQuote, FeeEvent, BankBooks,
  WebhookDelivery, WebhookEndpoint, WebhookEvent, WebhookEventType,
  Merchant, CardProgram, CardProgramInput, Card, IssueCardResult, CardAuthorization,
  DepositProduct, DepositProductInput, AccountInterest, FixedDeposit, BreakQuote, MaturityInstruction,
  LiveSnapshot, PlanCatalog, PlanId, LoginChallenge, LoginResult,
  LoanProduct, LoanProductInput, Loan, CreditScore, LoanQuote, LoanPayoffQuote,
} from "./types";

/** An error returned by the engine, in its standard error envelope. */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly correlationId?: string;
  readonly details?: Record<string, unknown>;

  constructor(status: number, code: string, message: string, correlationId?: string, details?: Record<string, unknown>) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.correlationId = correlationId;
    this.details = details;
  }

  static async fromResponse(res: Response): Promise<ApiError> {
    let body: unknown = null;
    try {
      body = await res.json();
    } catch {
      /* not JSON */
    }
    const e = (body as { error?: { code?: string; message?: string; correlation_id?: string; details?: Record<string, unknown> } } | null)?.error;
    return new ApiError(
      res.status,
      e?.code ?? `http_${res.status}`,
      e?.message ?? (res.statusText || "Request failed"),
      e?.correlation_id ?? res.headers.get("X-Request-Id") ?? undefined,
      e?.details,
    );
  }
}

export interface RequestOptions {
  body?: unknown;
  idempotencyKey?: string;
  headers?: Record<string, string>;
  signal?: AbortSignal;
}

type Query = Record<string, string | number | boolean | undefined | null>;

export function withQuery(path: string, q?: Query): string {
  if (!q) return path;
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(q)) {
    if (v !== undefined && v !== null && v !== "") p.set(k, String(v));
  }
  const s = p.toString();
  return s ? `${path}?${s}` : path;
}

export interface LiveQuery {
  environment?: Env;
  owner?: "player" | "merchant" | "system" | "all";
  bank_id?: string;
  q?: string;
  limit?: number;
}

/** Fired when the signed-in session's credential is rejected (revoked or expired). */
export const UNAUTHORIZED = "gbe:unauthorized";

/** Thin fetch wrapper: bearer auth, JSON in and out, typed errors. */
export class ApiClient {
  constructor(
    readonly baseUrl: string,
    readonly token: string,
    private readonly opts: { reportAuthFailures?: boolean; fetchImpl?: typeof fetch } = {},
  ) {}

  async request<T>(method: string, path: string, opts: RequestOptions = {}): Promise<T> {
    const headers: Record<string, string> = { Accept: "application/json", ...opts.headers };
    if (this.token) headers.Authorization = `Bearer ${this.token}`;
    if (opts.body !== undefined) headers["Content-Type"] = "application/json";
    if (opts.idempotencyKey) headers["Idempotency-Key"] = opts.idempotencyKey;
    let res: Response;
    try {
      const f = this.opts.fetchImpl ?? ((...a: Parameters<typeof fetch>) => fetch(...a));
      res = await f(this.baseUrl.replace(/\/$/, "") + path, {
        method,
        headers,
        body: opts.body === undefined ? undefined : JSON.stringify(opts.body),
        signal: opts.signal,
      });
    } catch (err) {
      if ((err as Error).name === "AbortError") throw err;
      throw new ApiError(0, "network_error", `Could not reach the engine at ${this.baseUrl || window.location.origin}. Check that it is running.`);
    }
    if (!res.ok) {
      if (res.status === 401 && this.opts.reportAuthFailures) window.dispatchEvent(new Event(UNAUTHORIZED));
      throw await ApiError.fromResponse(res);
    }
    if (res.status === 204) return undefined as T;
    const text = await res.text();
    return (text ? JSON.parse(text) : undefined) as T;
  }

  get<T>(path: string, q?: Query, signal?: AbortSignal) {
    return this.request<T>("GET", withQuery(path, q), { signal });
  }
  post<T>(path: string, body?: unknown, idempotencyKey?: string, headers?: Record<string, string>) {
    return this.request<T>("POST", path, { body: body ?? {}, idempotencyKey, headers });
  }
  patch<T>(path: string, body: unknown, headers?: Record<string, string>) {
    return this.request<T>("PATCH", path, { body, headers });
  }
  put<T>(path: string, body: unknown, headers?: Record<string, string>) {
    return this.request<T>("PUT", path, { body, headers });
  }
  del(path: string, headers?: Record<string, string>) {
    return this.request<void>("DELETE", path, { headers });
  }
}

/** The engine's base URL: build-time setting, else same origin (dev proxy). */
export function defaultBaseUrl(): string {
  return (import.meta.env?.VITE_GBE_API_URL ?? "").replace(/\/$/, "");
}

// ---------------------------------------------------------------------------
// Developer sign-up and sign-in (email and password)

export const authApi = (c: ApiClient) => ({
  register: (body: { name: string; email: string; password: string }) => c.post<SignInResult>("/v1/auth/register", body),
  login: (body: { email: string; password: string }) => c.post<LoginResult>("/v1/auth/login", body),
  verifyLoginCode: (body: { challenge_id: string; code: string }) => c.post<SignInResult>("/v1/auth/login/verify", body),
  resendLoginCode: (challengeId: string) => c.post<{ challenge: LoginChallenge }>("/v1/auth/login/resend", { challenge_id: challengeId }),
  logout: () => c.post<void>("/v1/auth/logout"),
  changePassword: (body: { current_password?: string; new_password: string }) => c.post<void>("/v1/developer/password", body),
  verifyEmail: (token: string) => c.post<{ developer: Developer }>("/v1/auth/verify-email", { token }),
  resendVerification: () => c.post<{ sent_to: string }>("/v1/auth/verify-email/resend"),
});

// ---------------------------------------------------------------------------
// Super admin (platform token)

export const adminApi = (c: ApiClient) => ({
  whoami: () => c.get<{ role: string }>("/v1/admin/whoami"),
  overview: () => c.get<Overview>("/v1/admin/overview"),
  developers: (q: { q?: string; status?: string; limit?: number; offset?: number }) => c.get<Page<AdminDeveloper>>("/v1/admin/developers", q),
  developer: (id: string) =>
    c.get<{ developer: AdminDeveloper; games: AdminGame[]; credentials: Credential[] }>(`/v1/admin/developers/${id}`),
  createDeveloper: (body: { name: string; contact_email: string }) =>
    c.post<{ developer: Developer; credential: Credential }>("/v1/admin/developers", body),
  updateDeveloper: (id: string, body: { name?: string; contact_email?: string; status?: string }) =>
    c.patch<AdminDeveloper>(`/v1/admin/developers/${id}`, body),
  setPlan: (id: string, body: { plan: PlanId; expires_at?: string | null }) => c.put<AdminDeveloper>(`/v1/admin/developers/${id}/plan`, body),
  issueCredential: (id: string, label: string) =>
    c.post<{ credential: Credential }>(`/v1/admin/developers/${id}/credentials`, { label }),
  revokeCredential: (id: string, credId: string) => c.del(`/v1/admin/developers/${id}/credentials/${credId}`),
  games: (q: { q?: string; status?: string; developer_id?: string; limit?: number; offset?: number }) => c.get<Page<AdminGame>>("/v1/admin/games", q),
  game: (id: string) => c.get<AdminGameDetail>(`/v1/admin/games/${id}`),
  setGameStatus: (id: string, status: string, reason?: string) => c.patch<AdminGameDetail>(`/v1/admin/games/${id}`, { status, reason }),
  revokeKey: (keyId: string, reason?: string) => c.del(withQuery(`/v1/admin/api-keys/${keyId}`, { reason })),
  auditLogs: (q: { developer_id?: string; game_id?: string; action?: string; environment?: string; before?: string; limit?: number }) =>
    c.get<{ data: AuditLog[] }>("/v1/admin/audit-logs", q),
  reconcile: (q: { game_id?: string; environment?: string }) => c.get<ReconReport>("/v1/admin/reconciliation", q),
  jobs: (q: { state?: string; limit?: number }) => c.get<{ data: JobRun[] }>("/v1/admin/jobs", q),
});

// ---------------------------------------------------------------------------
// Developer (developer credential)

export const developerApi = (c: ApiClient) => ({
  me: () => c.get<Developer>("/v1/developer"),
  overview: (gameId?: string) => c.get<DeveloperOverview>("/v1/developer/overview", { game_id: gameId }),
  updateMe: (body: { name: string }) => c.patch<Developer>("/v1/developer", body),
  sessions: () => c.get<{ data: DevSession[] }>("/v1/developer/sessions"),
  revokeSession: (id: string) => c.del(`/v1/developer/sessions/${id}`),
  signOutOthers: () => c.post<{ sessions_ended: number }>("/v1/developer/sessions/sign-out-others"),
  closeAccount: (body: { confirm_email: string; password?: string }) => c.post<void>("/v1/developer/close", body),
  plans: () => c.get<PlanCatalog>("/v1/plans"),
  startTrial: () => c.post<Developer>("/v1/developer/plan/trial"),
  /** A Stripe Checkout URL for Pro. */
  checkout: () => c.post<{ url: string }>("/v1/developer/billing/checkout"),
  /** A Stripe billing portal URL. */
  billingPortal: () => c.post<{ url: string }>("/v1/developer/billing/portal"),
  liveBalances: (gameId: string, q: LiveQuery, signal?: AbortSignal) => c.get<LiveSnapshot>(`/v1/games/${gameId}/live-balances`, { ...q }, signal),
  /** The URL of the live balance stream; read it with readEventStream. */
  liveBalanceStreamUrl: (gameId: string, q: LiveQuery & { since: string }) =>
    c.baseUrl.replace(/\/$/, "") + withQuery(`/v1/games/${gameId}/live-balances/stream`, { ...q }),
  credentials: () => c.get<{ data: Credential[] }>("/v1/developer/credentials"),
  issueCredential: (label: string, expiresAt?: string) =>
    c.post<Credential>("/v1/developer/credentials", expiresAt ? { label, expires_at: expiresAt } : { label }),
  revokeCredential: (id: string) => c.del(`/v1/developer/credentials/${id}`),
  games: () => c.get<{ data: Game[] }>("/v1/games"),
  game: (id: string) => c.get<Game>(`/v1/games/${id}`),
  createGame: (name: string) => c.post<Game>("/v1/games", { name }),
  updateGame: (id: string, body: { name?: string; slug?: string; status?: "active" | "suspended" }) => c.patch<Game>(`/v1/games/${id}`, body),
  deleteGame: (id: string) => c.del(`/v1/games/${id}`),
  keys: (gameId: string) => c.get<{ data: ApiKey[] }>(`/v1/games/${gameId}/api-keys`),
  issueKey: (gameId: string, body: { environment: Env; label: string; scopes: string[] }) =>
    c.post<{ api_key: ApiKey }>(`/v1/games/${gameId}/api-keys`, body),
  revokeKey: (gameId: string, keyId: string) => c.del(`/v1/games/${gameId}/api-keys/${keyId}`),
  auditLogs: (gameId: string, q: { environment?: string; limit?: number; before?: string }) =>
    c.get<{ data: AuditLog[] }>(`/v1/games/${gameId}/audit-logs`, q),
});

/**
 * Game-scoped configuration calls. With a developer credential these carry
 * X-Game-Id / X-Environment; with a game key they are not needed.
 */
export const gameApi = (c: ApiClient, scope?: { gameId: string; env: Env }) => {
  const h = scope ? { "X-Game-Id": scope.gameId, "X-Environment": scope.env } : undefined;
  const get = <T,>(path: string, q?: Query) => c.request<T>("GET", withQuery(path, q), { headers: h });
  return {
    currencies: () => get<{ data: Currency[] }>("/v1/currencies"),
    createCurrency: (body: { code: string; name: string; symbol?: string; exponent: number }) => c.post<Currency>("/v1/currencies", body, undefined, h),
    banks: () => get<{ data: Bank[] }>("/v1/banks"),
    bank: (id: string) => get<Bank>(`/v1/banks/${id}`),
    createBank: (body: { name: string; base_currency: string; bank_code?: string; branding?: Record<string, unknown> }) =>
      c.post<Bank>("/v1/banks", body, undefined, h),
    updateBank: (id: string, body: { name?: string; branding?: Record<string, unknown>; feature_flags?: Record<string, boolean>; status?: string }) =>
      c.patch<Bank>(`/v1/banks/${id}`, body, h),
    limits: (bankId: string) => get<Limits>(`/v1/banks/${bankId}/limits`),
    setLimits: (bankId: string, body: Partial<Record<string, LimitPolicy | null>>) => c.put<Limits>(`/v1/banks/${bankId}/limits`, body, h),
    fees: (bankId: string) => get<{ data: FeeRule[] }>(`/v1/banks/${bankId}/fees`),
    createFee: (bankId: string, body: FeeRuleInput) => c.post<FeeRule>(`/v1/banks/${bankId}/fees`, body, undefined, h),
    updateFee: (bankId: string, feeId: string, body: FeeRuleInput) => c.patch<FeeRule>(`/v1/banks/${bankId}/fees/${feeId}`, body, h),
    quoteFees: (body: { bank_id: string; event: FeeEvent; amount_minor: number }) => c.post<FeeQuote>("/v1/fees/quote", body, undefined, h),
    books: (bankId: string) => get<BankBooks>(`/v1/banks/${bankId}/books`),
    clock: () => get<Clock>("/v1/clock"),
    setClock: (body: { mode?: string; speed?: number; time_zone?: string; game_time?: string }) => c.put<Clock>("/v1/clock", body, h),
    advanceClock: (body: { minutes?: number; hours?: number; days?: number }) =>
      c.post<{ clock: Clock; jobs: { holds_expired: number; jobs_run: string[] } }>("/v1/clock/advance", body, undefined, h),
    reconcile: () => get<ReconReport>("/v1/reconciliation"),
    depositProducts: (bankId: string) => get<{ data: DepositProduct[] }>(`/v1/banks/${bankId}/deposit-products`),
    createDepositProduct: (bankId: string, body: DepositProductInput) => c.post<DepositProduct>(`/v1/banks/${bankId}/deposit-products`, body, undefined, h),
    updateDepositProduct: (bankId: string, id: string, body: DepositProductInput) => c.patch<DepositProduct>(`/v1/banks/${bankId}/deposit-products/${id}`, body, h),
    quoteDeposit: (body: { product_id: string; amount_minor: number; days?: number; term_days?: number }) =>
      c.post<{ gross_interest_minor: number; tax_minor: number; net_interest_minor: number; rate_bps: number; days: number }>("/v1/deposit-products/quote", body, undefined, h),
    loanProducts: (bankId: string) => get<{ data: LoanProduct[] }>(`/v1/banks/${bankId}/loan-products`),
    createLoanProduct: (bankId: string, body: LoanProductInput) => c.post<LoanProduct>(`/v1/banks/${bankId}/loan-products`, body, undefined, h),
    updateLoanProduct: (bankId: string, id: string, body: LoanProductInput) => c.patch<LoanProduct>(`/v1/banks/${bankId}/loan-products/${id}`, body, h),
    loans: (q: { player_id?: string; account_id?: string; bank_id?: string; status?: string; limit?: number }) => get<{ data: Loan[] }>("/v1/loans", q),
    loan: (id: string) => get<Loan>(`/v1/loans/${id}`),
    creditScore: (playerId: string) => get<CreditScore>(`/v1/players/${playerId}/credit-score`),
    quoteLoan: (body: { account_id: string; product_id: string; amount_minor: number; term_days: number; external_score?: number }) =>
      c.post<LoanQuote>("/v1/loans/quote", body, undefined, h),
    cardPrograms: (bankId: string) => get<{ data: CardProgram[] }>(`/v1/banks/${bankId}/card-programs`),
    createCardProgram: (bankId: string, body: CardProgramInput) => c.post<CardProgram>(`/v1/banks/${bankId}/card-programs`, body, undefined, h),
    updateCardProgram: (bankId: string, id: string, body: CardProgramInput) => c.patch<CardProgram>(`/v1/banks/${bankId}/card-programs/${id}`, body, h),
    webhookEndpoints: () => get<{ data: WebhookEndpoint[] }>("/v1/webhooks/endpoints"),
    createWebhookEndpoint: (body: { url: string; description?: string; event_types?: (WebhookEventType | "*")[] }) =>
      c.post<WebhookEndpoint>("/v1/webhooks/endpoints", body, undefined, h),
    updateWebhookEndpoint: (id: string, body: { url?: string; description?: string; event_types?: (WebhookEventType | "*")[]; status?: "active" | "disabled" }) =>
      c.patch<WebhookEndpoint>(`/v1/webhooks/endpoints/${id}`, body, h),
    deleteWebhookEndpoint: (id: string) => c.del(`/v1/webhooks/endpoints/${id}`, h),
    rotateWebhookSecret: (id: string) => c.post<WebhookEndpoint>(`/v1/webhooks/endpoints/${id}/rotate-secret`, undefined, undefined, h),
    testWebhookEndpoint: (id: string) => c.post<WebhookDelivery>(`/v1/webhooks/endpoints/${id}/test`, undefined, undefined, h),
    webhookEvents: (q: { type?: string; account_id?: string; limit?: number; cursor?: string }) =>
      get<{ data: WebhookEvent[]; next_cursor: string | null; has_more: boolean }>("/v1/webhooks/events", q),
    webhookDeliveries: (eventId: string) => get<{ data: WebhookDelivery[] }>(`/v1/webhooks/events/${eventId}/deliveries`),
    redeliverWebhookEvent: (eventId: string, endpointId?: string) =>
      c.post<{ data: WebhookDelivery[] }>(`/v1/webhooks/events/${eventId}/redeliver`, endpointId ? { endpoint_id: endpointId } : {}, undefined, h),

    // Runtime calls (game key only).
    upsertPlayer: (body: { external_player_id: string; display_name?: string }) => c.post<Player>("/v1/players", body),
    accounts: (q: { bank_id?: string; include_system?: boolean; limit?: number }) => get<{ data: Account[] }>("/v1/accounts", q),
    openAccount: (body: { bank_id: string; player_id?: string; merchant_id?: string; account_name?: string; product_id?: string }) => c.post<Account>("/v1/accounts", body),
    accountInterest: (id: string) => get<AccountInterest>(`/v1/accounts/${id}/interest`),
    fixedDeposits: (q: { source_account_id?: string; player_id?: string }) => get<{ data: FixedDeposit[] }>("/v1/fixed-deposits", q),
    openFixedDeposit: (body: { source_account_id: string; product_id: string; term_days: number; amount_minor: number; maturity_instruction?: MaturityInstruction }, key: string) =>
      c.post<{ fixed_deposit: FixedDeposit }>("/v1/fixed-deposits", body, key),
    applyLoan: (body: { account_id: string; product_id: string; amount_minor: number; term_days: number; external_score?: number; max_rate_bps?: number }, key: string) =>
      c.post<{ loan: Loan }>("/v1/loans", body, key),
    repayLoan: (id: string, amount: number, key: string) => c.post<{ loan: Loan }>(`/v1/loans/${id}/repay`, { amount_minor: amount }, key),
    payoffQuote: (id: string) => get<LoanPayoffQuote>(`/v1/loans/${id}/payoff-quote`),
    payoffLoan: (id: string, max: number, key: string) => c.post<{ loan: Loan }>(`/v1/loans/${id}/payoff`, { max_amount_minor: max }, key),
    writeOffLoan: (id: string, reason: string, key: string) => c.post<{ loan: Loan }>(`/v1/loans/${id}/write-off`, { reason }, key),
    breakQuote: (id: string) => get<BreakQuote>(`/v1/fixed-deposits/${id}/break-quote`),
    breakFixedDeposit: (id: string, maxPenalty: number, key: string) =>
      c.post<{ fixed_deposit: FixedDeposit; quote: BreakQuote }>(`/v1/fixed-deposits/${id}/break`, { max_penalty_minor: maxPenalty }, key),
    createMerchant: (body: { external_merchant_id: string; name: string; category?: string }) => c.post<Merchant>("/v1/merchants", body),
    merchants: () => get<{ data: Merchant[] }>("/v1/merchants"),
    cards: (q: { account_id?: string }) => get<{ data: Card[] }>("/v1/cards", q),
    issueCard: (body: { account_id: string; name_on_card?: string }, key: string) => c.post<IssueCardResult>("/v1/cards", body, key),
    cardAction: (id: string, action: "block" | "unblock" | "report-lost" | "close", reason?: string) =>
      c.post<Card>(`/v1/cards/${id}/${action}`, reason ? { reason } : {}),
    cardAuthorizations: (q: { account_id?: string; merchant_id?: string; card_id?: string; limit?: number }) =>
      get<{ data: CardAuthorization[] }>("/v1/card-authorizations", q),
    authorizeCard: (body: { card_id: string; merchant_account_id: string; amount_minor: number; description?: string; capture?: boolean }, key: string) =>
      c.post<{ authorization: CardAuthorization; fees_total_minor: number }>("/v1/card-authorizations", body, key),
    captureCard: (id: string, body: { amount_minor?: number }, key: string) =>
      c.post<{ authorization: CardAuthorization; fees_total_minor: number }>(`/v1/card-authorizations/${id}/capture`, body, key),
    releaseCard: (id: string, key: string) => c.post<{ authorization: CardAuthorization }>(`/v1/card-authorizations/${id}/release`, {}, key),
    refundCard: (id: string, body: { amount_minor?: number; reason?: string }, key: string) =>
      c.post<{ authorization: CardAuthorization }>(`/v1/card-authorizations/${id}/refunds`, body, key),
    setAccountStatus: (id: string, status: string, reason?: string) => c.patch<Account>(`/v1/accounts/${id}`, { status, reason }),
    balance: (id: string) => get<Balance>(`/v1/accounts/${id}/balance`),
    history: (id: string, q: { limit?: number; cursor?: string }) =>
      get<{ data: HistoryItem[]; next_cursor: string | null; has_more: boolean }>(`/v1/accounts/${id}/transactions`, q),
    holds: (accountId: string, status?: string) => get<{ data: Hold[] }>(`/v1/accounts/${accountId}/holds`, { status }),
    nameEnquiry: (bank_code: string, account_number: string) => get<NameEnquiry>("/v1/name-enquiry", { bank_code, account_number }),
    fund: (body: { account_id: string; amount_minor: number; reason: string; reference: string; narration?: string }, key: string) =>
      c.post<{ transaction: Transaction; balance: Balance }>("/v1/funding", body, key),
    transfer: (body: { source_account_id: string; destination_account_number?: string; destination_account_id?: string; amount_minor: number; narration?: string; max_fee_minor?: number }, key: string) =>
      c.post<{ transaction: Transaction; fees_total_minor: number; total_debit_minor: number }>("/v1/transfers", body, key),
    placeHold: (body: { account_id: string; amount_minor: number; reason: string; destination_account_number?: string; expires_in_game_seconds?: number }, key: string) =>
      c.post<{ hold: Hold; balance: Balance }>("/v1/holds", body, key),
    captureHold: (id: string, body: { amount_minor?: number; narration?: string }, key: string) =>
      c.post<{ hold: Hold; transaction: Transaction; fees_total_minor: number }>(`/v1/holds/${id}/capture`, body, key),
    releaseHold: (id: string, key: string) => c.post<{ hold: Hold }>(`/v1/holds/${id}/release`, {}, key),
    transaction: (txId: string) => get<Transaction>(`/v1/transactions/${txId}`),
    reverse: (txId: string, reason: string, key: string) => c.post<Transaction>(`/v1/transactions/${txId}/reverse`, { reason }, key),
  };
};

export type AdminApi = ReturnType<typeof adminApi>;
export type DeveloperApi = ReturnType<typeof developerApi>;
export type GameApi = ReturnType<typeof gameApi>;
